import { claimApi, partTypeApi, procurementApi } from '@/main/api';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import AreaDeadlineAnswerRequote from '@/main/components/shared/areaDeadlineAnswerRequote/areaDeadlineAnswerRequote';
import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import HubContainer from '@/main/components/shared/hub/HubContainer';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { KeyValueDefault } from '@/main/models/common';
import {
  ReasonFilterRequest,
  RequotePartClaimRequest
} from '@/main/models/request';
import { ParameterFilterRequest } from '@/main/models/request/parameterFilterRequest';
import {
  GetItemsCancelledBySupplierResponse,
  GetQuotePartTypesByDirectAssignCriteriaResponse
} from '@/main/models/response';
import {
  ICancelledPartRequoteSchema,
  INITIAL_VALUES_FORM_REQUOTE,
  RequoteCancelledPartsForm,
  requotePartsCancelledSchema
} from '@/main/screens/order/cancelledParts/components/modalRequotePartsCancelled/modalRequotePartsCancelledUtils';
import RowItemRequoteCancelled from '@/main/screens/order/cancelledParts/components/modalRequotePartsCancelled/rowItemRequoteCancelled/rowItemRequoteCancelled';
import { Controls } from '@/main/services/constants/constantsControl';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { SendUpdatesSignalRTypeEnum } from '@/main/services/enum/SendUpdatesSignalRTypeEnum';
import { SystemApplicationEnum } from '@/main/services/enum/SystemApplicationEnum';
import { isNullUndefined } from '@/main/services/reducerUtils';
import {
  getDefaultCountryOptionDrop,
  getTranslateByKey,
  isEmpty,
  isNullUndefinedEmpty,
  isShowControlByElementId
} from '@/main/services/utils';
import { convertStringForBoolean } from '@/main/services/utilsString';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getParameterByTypeAndCompany,
  useParameterReducer
} from '@/main/store/parameter';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import { getReason, useReasonReducer } from '@/main/store/reason';
import { signalRClearReceiveUpdatesByKey } from '@/main/store/signalR';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as S from './styles';

interface IModalRequotePartsCancelled {
  isOpen: boolean;
  quoteGuids: string[];
  procurementSupplierItemGuids: string[];
  handleClose: (refreshGrid: boolean) => void;
  guidCompany: string;
}

function ModalRequotePartsCancelledContent({
  quoteGuids,
  isOpen,
  procurementSupplierItemGuids,
  handleClose,
  guidCompany
}: Readonly<IModalRequotePartsCancelled>) {
  const [isLoadingGet, setIsLoadingGet] = useState<boolean>(true);
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const [isLoadingSignalR, setIsLoadingSignalR] = useState<boolean>(false);
  const [isErrorSignalR, setIsErrorSignalR] = useState<boolean>(false);
  const [partsData, setPartsData] = useState<
    GetItemsCancelledBySupplierResponse[]
  >([]);

  const [partTypeOptions, setPartTypeOptions] = useState<
    GetQuotePartTypesByDirectAssignCriteriaResponse[]
  >([]);

  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

  const { reason: reasonReducer } = useReasonReducer();
  const { countriesUser: countryReducer } = useCountryReducer();
  const { parameterByTypeAndCompany: parameterReducer } = useParameterReducer();

  const minDeadline = useParameterByTypeScreenReducer(
    ParameterTypeEnum.MinDeadlineAnswerRequote
  );

  const maxDeadline = useParameterByTypeScreenReducer(
    ParameterTypeEnum.MaxDeadlineAnswerRequote
  );

  const dispatch = useDispatch();

  const methods = useForm<RequoteCancelledPartsForm>({
    mode: 'onChange',
    resolver: zodResolver(requotePartsCancelledSchema),
    defaultValues: INITIAL_VALUES_FORM_REQUOTE
  });

  const { control, watch, handleSubmit, setValue } = methods;

  const { fields: partFields, replace } = useFieldArray({
    control,
    name: 'parts'
  });
  const watcherReasonType = watch('reason');
  const isReasonOther = convertStringForBoolean(watcherReasonType?.default);

  function loadPartTypes() {
    if (isNullUndefined(quoteGuids)) return;
    partTypeApi
      .getPartTypeByDirectAssignCriteriaCancelledParts(quoteGuids)
      .then(res => {
        const {
          data: { result }
        } = res;

        if (result) {
          setPartTypeOptions(result);
        }
      });
  }

  function loadParts() {
    procurementApi
      .getItemsCancelledBySupplierDetails(procurementSupplierItemGuids)
      .then(res => {
        const {
          data: { result }
        } = res;

        if (result) {
          const mappedToForm: ICancelledPartRequoteSchema[] = result.map(
            part => ({
              partType: isEmpty(part.quoteProcurementPartInfo.idPartType)
                ? null
                : {
                    key: part.quoteProcurementPartInfo.idPartType,
                    value: part.quoteProcurementPartInfo.partType ?? '',
                    default: String(part.quoteProcurementPartInfo.idPartType)
                  }
            })
          );
          replace(mappedToForm);
          setPartsData(result);
        }
      })
      .finally(() => {
        setIsLoadingGet(false);
      });
  }

  function validateDeadlineAnswer(deadlineAnswer: number) {
    if (deadlineAnswer >= minDeadline && deadlineAnswer <= maxDeadline)
      return true;

    const messageError = getTranslateByKey(
      'quote.requoteDeadlineAnswerInvalid',
      true
    )
      .replace('{0}', minDeadline.toString())
      .replace('{1}', maxDeadline.toString())
      .replace('{2}', (deadlineAnswer || '').toString());

    toast.error(messageError);
    return false;
  }

  async function onSubmit(
    data: RequoteCancelledPartsForm,
    isNational: boolean = false
  ) {
    if (!validateDeadlineAnswer(Number(data.deadlineAnswer))) return;

    const message = `${getTranslateByKey('information.confirmRequoteOrdered')} ${getTranslateByKey('information.thisActionIsIrreversible')}`;
    const isConfirmed = await getConfirmation(message);
    if (!isConfirmed) return;

    setIsLoadingSave(true);
    setIsErrorSignalR(false);

    dispatch(
      signalRClearReceiveUpdatesByKey(
        SendUpdatesSignalRTypeEnum.RequoteItemsMultiClaim
      )
    );

    const request: RequotePartClaimRequest = {
      isNational: isNational,
      deadlineAnswerRequote: Number(data.deadlineAnswer),
      requoteItems: data.parts.map((part, index) => {
        const partData = partsData[index];
        return {
          guidProcurementSupplierItem: partData.procurementSupplierItemGuid,
          idPartType: part.partType?.default
            ? Number(part.partType.default)
            : null,
          replacePartType: true,
          guidReason: String(data.reason?.key),
          reasonDescription: data.reasonDescription ?? ''
        };
      })
    };

    claimApi.requotePartsCancelledBySupplier(request).finally(() => {
      setIsLoadingSave(false);
      setIsLoadingSignalR(true);
    });
  }

  function onFinishedRequote() {
    if (isLoadingSignalR) {
      setIsLoadingSignalR(false);
      handleClose(true);
    }
  }

  function onErrorRequote() {
    setIsLoadingSignalR(false);
    setIsErrorSignalR(true);
  }

  useEffect(() => {
    if (
      isOpen &&
      !isNullUndefined(quoteGuids) &&
      !isNullUndefined(procurementSupplierItemGuids)
    ) {
      loadPartTypes();
      loadParts();
      if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
    }
  }, [isOpen, quoteGuids, procurementSupplierItemGuids]);

  useEffect(() => {
    if (!isNullUndefinedEmpty(guidCompany)) {
      const request: ParameterFilterRequest = {
        idParameterType: ParameterTypeEnum.TimeSupplierManuelAnswerQuote,
        guidCompany: guidCompany,
        applicationType: SystemApplicationEnum.Parts
      };

      dispatch(getParameterByTypeAndCompany(request));
    }
  }, [guidCompany]);

  useEffect(() => {
    if (isNullUndefined(parameterReducer.data)) return;

    let initialDeadline = Number(parameterReducer.data?.at(0)?.value);
    if (initialDeadline < minDeadline) initialDeadline = minDeadline;
    if (initialDeadline > maxDeadline) initialDeadline = maxDeadline;

    if (initialDeadline) {
      setValue('deadlineAnswer', String(initialDeadline));
    }
  }, [JSON.stringify(parameterReducer.data)]);

  useEffect(() => {
    const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
    if (countryUser === null) return;

    const requestReason: ReasonFilterRequest = {
      guidCountry: String(countryUser.key),
      idReasonType: ReasonTypeEnum.RequoteProcurement
    };
    dispatch(getReason(requestReason));
  }, [JSON.stringify(countryReducer.data)]);

  const partTypesByQuote = useMemo(
    () => new Map(partTypeOptions.map(p => [p.guidQuote, p.partTypes])),
    [partTypeOptions]
  );

  if (isLoadingGet) return <LoadingComponent />;

  if (partFields.length === 0) return <ResultNotFoundComponent />;

  return (
    <FormProvider {...methods}>
      <form noValidate>
        {!isLoadingSignalR &&
          partFields.map((field, index) => {
            const partData = partsData[index];
            if (!partData) return null;
            return (
              <RowItemRequoteCancelled
                key={partData.procurementSupplierItemGuid}
                partData={partData}
                index={index}
                partTypeOptions={partTypesByQuote.get(partData.quoteGuid) ?? []}
              />
            );
          })}

        <S.AreaFooter>
          {isLoadingSignalR || isErrorSignalR ? (
            <HubContainer
              type={SendUpdatesSignalRTypeEnum.RequoteItemsMultiClaim}
              customProps={{
                onFinishedRequote: onFinishedRequote,
                onErrorRequote: onErrorRequote
              }}
            />
          ) : (
            <>
              <AreaDeadlineAnswerRequote
                min={minDeadline}
                max={maxDeadline}
                control={control}
              />
              <S.AreaReason>
                <KogAutoCompleteControlled
                  id="id-cancelledPartsRequote-select-reason"
                  nameCustom="reason"
                  isDefaultValueUnique
                  label={getTranslateByKey('information.reason')}
                  control={control}
                  rulesCustom={{ required: true }}
                  setValueCustom={setValue}
                  options={reasonReducer.data}
                  isLoading={reasonReducer.isLoading}
                  onChangeCustom={option => {
                    if (option === null) return;

                    if (
                      convertStringForBoolean(
                        (option as KeyValueDefault)?.default
                      )
                    ) {
                      setValue('reasonDescription', '');
                    }
                  }}
                />
                {isReasonOther && (
                  <KogInputTextControlledNew
                    required={isReasonOther}
                    id="id-cancelledPartsRequote-input-description"
                    label={getTranslateByKey('information.description')}
                    name="reasonDescription"
                    control={control}
                  />
                )}
              </S.AreaReason>

              <S.AreaButtons>
                <SecondaryButton
                  id="id-cancelledPartsRequote-btn-requote-back"
                  type="button"
                  onClick={() => handleClose(false)}>
                  {getTranslateByKey('information.back')}
                </SecondaryButton>
                <Button
                  id="id-cancelledPartsRequote-btn-requote-save"
                  isLoading={isLoadingSave}
                  onClick={handleSubmit(data => onSubmit(data))}>
                  {getTranslateByKey('information.reQuote')}
                </Button>
                <Button
                  id="id-cancelledPartsRequote-btn-requote-national-save"
                  isLoading={isLoadingSave}
                  onClick={handleSubmit(data => onSubmit(data, true))}>
                  {getTranslateByKey('information.requoteNational')}
                </Button>
              </S.AreaButtons>
            </>
          )}
        </S.AreaFooter>
      </form>
      <ModalConfirmation />
    </FormProvider>
  );
}

export default function ModalRequotePartsCancelled(
  props: Readonly<IModalRequotePartsCancelled>
) {
  return (
    <KogModalSideComponent
      shouldRenderOpen
      open={props.isOpen}
      closeModal={() => props.handleClose(false)}>
      {props.isOpen && <ModalRequotePartsCancelledContent {...props} />}
    </KogModalSideComponent>
  );
}
